import { z } from 'zod';
import {
  acceptInviteResponseSchema,
  healthResponseSchema,
  inviteLookupResponseSchema,
  listAuditResponseSchema,
  listModelsResponseSchema,
  listUsersResponseSchema,
  listVoicesResponseSchema,
  loginResponseSchema,
  logoutResponseSchema,
  meResponseSchema,
  settingsResponseSchema,
  settingsStatusResponseSchema,
  testElevenLabsResponseSchema,
  testGcsResponseSchema,
  type AcceptInviteRequest,
  type AcceptInviteResponse,
  type DisableUserResponse,
  type EnableUserResponse,
  type HealthResponse,
  type InviteLookupResponse,
  type InviteUserRequest,
  type InviteUserResponse,
  type ListAuditQuery,
  type ListAuditResponse,
  type ListModelsResponse,
  type ListUsersResponse,
  type ListVoicesResponse,
  type LoginRequest,
  type LoginResponse,
  type LogoutResponse,
  type MeResponse,
  type SettingsResponse,
  type SettingsStatusResponse,
  type TestElevenLabsResponse,
  type TestGcsResponse,
  type UpdateSettingsRequest,
} from '@tekvoice/shared';
import {
  abortRunResponseSchema,
  approveRowResponseSchema,
  assetResponseSchema,
  batchDetailResponseSchema,
  bulkApproveResponseSchema,
  createManualRowResponseSchema,
  deleteBatchResponseSchema,
  disableUserResponseSchema,
  enableUserResponseSchema,
  generationEstimateResponseSchema,
  importCommitResponseSchema,
  importPreviewResponseSchema,
  inviteUserResponseSchema,
  listBatchesResponseSchema,
  listRowsResponseSchema,
  listRunsResponseSchema,
  listTakesResponseSchema,
  progressResponseSchema,
  regenerateAssetResponseSchema,
  removeRowResponseSchema,
  rowHistoryResponseSchema,
  rowResponseSchema,
  selectTakeResponseSchema,
  startGenerationResponseSchema,
  unapproveRowResponseSchema,
  updateBatchResponseSchema,
  updateRowOverrideResponseSchema,
  updateRowResponseSchema,
  type AbortRunResponse,
  type ApproveRowResponse,
  type AssetResponse,
  type BatchDetailResponse,
  type BulkApproveResponse,
  type CreateManualRowRequest,
  type CreateManualRowResponse,
  type DeleteBatchResponse,
  type GenerationEstimateRequest,
  type GenerationEstimateResponse,
  type GenerationScope,
  type ImportCommitRequest,
  type ImportCommitResponse,
  type ImportPreviewResponse,
  type ListBatchesQuery,
  type ListBatchesResponse,
  type ListRowsQuery,
  type ListRowsResponse,
  type ListRunsResponse,
  type ListTakesResponse,
  type ProgressResponse,
  type RegenerateAssetResponse,
  type RemoveRowResponse,
  type RowHistoryResponse,
  type RowResponse,
  type SelectTakeRequest,
  type SelectTakeResponse,
  type StartGenerationResponse,
  type UnapproveRowResponse,
  type UpdateBatchRequest,
  type UpdateBatchResponse,
  type UpdateRowOverrideRequest,
  type UpdateRowOverrideResponse,
  type UpdateRowRequest,
  type UpdateRowResponse,
} from '@tekvoice/shared';

export const API_BASE = '/api';

/** Every non-2xx response is normalised to this. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string | null = null,
    readonly details: unknown = null,
    /** Raw parsed error body — feature code re-parses it with the shared error schema. */
    readonly body: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

interface RequestOptions<TSchema extends z.ZodTypeAny> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Multipart payload (CSV import); mutually exclusive with `body`. */
  formData?: FormData;
  query?: Record<string, string | number | boolean | undefined>;
  schema: TSchema;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions<z.ZodTypeAny>['query']): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${url}?${qs}` : url;
}

/**
 * Thin fetch wrapper: sends the session cookie and parses every response with
 * the shared zod schema, so the client can never drift from the contract.
 */
export async function apiRequest<TSchema extends z.ZodTypeAny>(
  path: string,
  options: RequestOptions<TSchema>,
): Promise<z.infer<TSchema>> {
  const response = await fetch(buildUrl(path, options.query), {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers: {
      accept: 'application/json',
      // FormData sets its own multipart boundary — never override it.
      ...(options.body !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
    ...(options.formData !== undefined ? { body: options.formData } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  const text = await response.text();
  const payload: unknown = text.length > 0 ? safeJsonParse(text) : null;

  if (!response.ok) throw errorFrom(response, payload);

  const parsed = options.schema.safeParse(payload);
  if (!parsed.success) {
    throw new ApiError(
      `Unexpected response shape for ${path}: ${parsed.error.issues
        .map((i) => `${i.path.join('.')}: ${i.message}`)
        .join('; ')}`,
      response.status,
      'contract_mismatch',
      parsed.error.issues,
    );
  }
  return parsed.data as z.infer<TSchema>;
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { message: text.slice(0, 300) };
  }
}

function errorFrom(response: Response, payload: unknown): ApiError {
  const record = (payload ?? {}) as Record<string, unknown>;
  const rawMessage = record.message;
  const message = Array.isArray(rawMessage)
    ? rawMessage.join(', ')
    : typeof rawMessage === 'string'
      ? rawMessage
      : response.statusText || 'Request failed';
  return new ApiError(
    message,
    response.status,
    typeof record.code === 'string' ? record.code : null,
    record.details ?? null,
    payload,
  );
}

export interface DownloadedFile {
  blob: Blob;
  filename: string;
}

const FILENAME_RE = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i;

/**
 * Binary/CSV endpoints (SPEC §7 export) — the response *is* the file, so there is
 * no schema to parse; errors still come back as the JSON envelope.
 */
export async function apiDownload(
  path: string,
  options: { method?: 'GET' | 'POST'; body?: unknown; fallbackFilename: string } ,
): Promise<DownloadedFile> {
  const response = await fetch(buildUrl(path), {
    method: options.method ?? 'POST',
    credentials: 'include',
    headers: {
      accept: 'text/csv',
      ...(options.body !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
  });

  if (!response.ok) {
    const text = await response.text();
    throw errorFrom(response, text.length > 0 ? safeJsonParse(text) : null);
  }

  const disposition = response.headers.get('content-disposition') ?? '';
  const match = FILENAME_RE.exec(disposition);
  return {
    blob: await response.blob(),
    filename: match ? decodeURIComponent(match[1]) : options.fallbackFilename,
  };
}


export const api = {
  auth: {
    login: (body: LoginRequest): Promise<LoginResponse> =>
      apiRequest('/auth/login', { method: 'POST', body, schema: loginResponseSchema }),
    logout: (): Promise<LogoutResponse> =>
      apiRequest('/auth/logout', { method: 'POST', schema: logoutResponseSchema }),
    me: (signal?: AbortSignal): Promise<MeResponse> =>
      apiRequest('/auth/me', { schema: meResponseSchema, signal }),
    lookupInvite: (token: string): Promise<InviteLookupResponse> =>
      apiRequest('/auth/accept-invite', {
        query: { token },
        schema: inviteLookupResponseSchema,
      }),
    acceptInvite: (body: AcceptInviteRequest): Promise<AcceptInviteResponse> =>
      apiRequest('/auth/accept-invite', {
        method: 'POST',
        body,
        schema: acceptInviteResponseSchema,
      }),
  },

  users: {
    list: (): Promise<ListUsersResponse> =>
      apiRequest('/users', { schema: listUsersResponseSchema }),
    invite: (body: InviteUserRequest): Promise<InviteUserResponse> =>
      apiRequest('/users/invite', { method: 'POST', body, schema: inviteUserResponseSchema }),
    reinvite: (id: string): Promise<InviteUserResponse> =>
      apiRequest(`/users/${id}/reinvite`, { method: 'POST', schema: inviteUserResponseSchema }),
    disable: (id: string): Promise<DisableUserResponse> =>
      apiRequest(`/users/${id}/disable`, { method: 'POST', schema: disableUserResponseSchema }),
    enable: (id: string): Promise<EnableUserResponse> =>
      apiRequest(`/users/${id}/enable`, { method: 'POST', schema: enableUserResponseSchema }),
  },

  settings: {
    get: (): Promise<SettingsResponse> => apiRequest('/settings', { schema: settingsResponseSchema }),
    update: (body: UpdateSettingsRequest): Promise<SettingsResponse> =>
      apiRequest('/settings', { method: 'PUT', body, schema: settingsResponseSchema }),
    status: (): Promise<SettingsStatusResponse> =>
      apiRequest('/settings/status', { schema: settingsStatusResponseSchema }),
    testElevenLabs: (): Promise<TestElevenLabsResponse> =>
      apiRequest('/settings/test-elevenlabs', {
        method: 'POST',
        schema: testElevenLabsResponseSchema,
      }),
    testGcs: (): Promise<TestGcsResponse> =>
      apiRequest('/settings/test-gcs', { method: 'POST', schema: testGcsResponseSchema }),
    models: (): Promise<ListModelsResponse> =>
      apiRequest('/settings/models', { schema: listModelsResponseSchema }),
    voices: (): Promise<ListVoicesResponse> =>
      apiRequest('/settings/voices', { schema: listVoicesResponseSchema }),
  },

  audit: {
    list: (query: Partial<ListAuditQuery> = {}): Promise<ListAuditResponse> =>
      apiRequest('/audit', {
        query: query as Record<string, string | number | boolean | undefined>,
        schema: listAuditResponseSchema,
      }),
  },

  health: {
    check: (deep = false): Promise<HealthResponse> =>
      apiRequest('/health', { query: { deep: deep ? '1' : undefined }, schema: healthResponseSchema }),
  },

  batches: {
    list: (query: Partial<ListBatchesQuery> = {}): Promise<ListBatchesResponse> =>
      apiRequest('/batches', {
        query: query as Record<string, string | number | boolean | undefined>,
        schema: listBatchesResponseSchema,
      }),
    get: (id: string): Promise<BatchDetailResponse> =>
      apiRequest(`/batches/${id}`, { schema: batchDetailResponseSchema }),
    rename: (id: string, body: UpdateBatchRequest): Promise<UpdateBatchResponse> =>
      apiRequest(`/batches/${id}`, { method: 'PATCH', body, schema: updateBatchResponseSchema }),
    remove: (id: string): Promise<DeleteBatchResponse> =>
      apiRequest(`/batches/${id}`, { method: 'DELETE', schema: deleteBatchResponseSchema }),
    rows: (id: string, query: Partial<ListRowsQuery> = {}): Promise<ListRowsResponse> =>
      apiRequest(`/batches/${id}/rows`, {
        query: query as Record<string, string | number | boolean | undefined>,
        schema: listRowsResponseSchema,
      }),
    createRow: (id: string, body: CreateManualRowRequest): Promise<CreateManualRowResponse> =>
      apiRequest(`/batches/${id}/rows`, {
        method: 'POST',
        body,
        schema: createManualRowResponseSchema,
      }),
    progress: (id: string, signal?: AbortSignal): Promise<ProgressResponse> =>
      apiRequest(`/batches/${id}/progress`, { schema: progressResponseSchema, signal }),
    runs: (id: string): Promise<ListRunsResponse> =>
      apiRequest(`/batches/${id}/runs`, { schema: listRunsResponseSchema }),
    estimate: (id: string, scope: GenerationScope): Promise<GenerationEstimateResponse> =>
      // SPEC §6 types the estimate with a request *body* (`generationEstimateRequestSchema`),
      // which a GET cannot carry. The API therefore exposes both verbs: GET
      // `?scope=all|missing|failed|stale` for the four literal scopes, POST
      // `{scope:{rowIds}}` for a row selection. Match it exactly.
      typeof scope === 'string'
        ? apiRequest(`/batches/${id}/generate/estimate`, {
            query: { scope },
            schema: generationEstimateResponseSchema,
          })
        : apiRequest(`/batches/${id}/generate/estimate`, {
            method: 'POST',
            body: { scope } satisfies GenerationEstimateRequest,
            schema: generationEstimateResponseSchema,
          }),
    generate: (id: string, scope: GenerationScope): Promise<StartGenerationResponse> =>
      apiRequest(`/batches/${id}/generate`, {
        method: 'POST',
        body: { scope },
        schema: startGenerationResponseSchema,
      }),
    approveBulk: (id: string, rowIds: string[]): Promise<BulkApproveResponse> =>
      apiRequest(`/batches/${id}/approve-bulk`, {
        method: 'POST',
        body: { rowIds },
        schema: bulkApproveResponseSchema,
      }),
    export: (id: string, fallbackFilename: string): Promise<DownloadedFile> =>
      apiDownload(`/batches/${id}/export`, { method: 'POST', body: {}, fallbackFilename }),
  },

  imports: {
    preview: (file: File): Promise<ImportPreviewResponse> => {
      const formData = new FormData();
      formData.append('file', file);
      return apiRequest('/batches/import/preview', {
        method: 'POST',
        formData,
        schema: importPreviewResponseSchema,
      });
    },
    commit: (body: ImportCommitRequest): Promise<ImportCommitResponse> =>
      apiRequest('/batches/import/commit', {
        method: 'POST',
        body,
        schema: importCommitResponseSchema,
      }),
  },

  rows: {
    get: (id: string): Promise<RowResponse> => apiRequest(`/rows/${id}`, { schema: rowResponseSchema }),
    update: (id: string, body: UpdateRowRequest): Promise<UpdateRowResponse> =>
      apiRequest(`/rows/${id}`, { method: 'PATCH', body, schema: updateRowResponseSchema }),
    updateOverride: (
      id: string,
      body: UpdateRowOverrideRequest,
    ): Promise<UpdateRowOverrideResponse> =>
      apiRequest(`/rows/${id}/override`, {
        method: 'PATCH',
        body,
        schema: updateRowOverrideResponseSchema,
      }),
    approve: (id: string, version: number): Promise<ApproveRowResponse> =>
      apiRequest(`/rows/${id}/approve`, {
        method: 'POST',
        body: { version },
        schema: approveRowResponseSchema,
      }),
    unapprove: (id: string): Promise<UnapproveRowResponse> =>
      apiRequest(`/rows/${id}/unapprove`, { method: 'POST', schema: unapproveRowResponseSchema }),
    remove: (id: string): Promise<RemoveRowResponse> =>
      apiRequest(`/rows/${id}`, { method: 'DELETE', schema: removeRowResponseSchema }),
    history: (id: string): Promise<RowHistoryResponse> =>
      apiRequest(`/rows/${id}/history`, { schema: rowHistoryResponseSchema }),
  },

  assets: {
    get: (id: string): Promise<AssetResponse> =>
      apiRequest(`/assets/${id}`, { schema: assetResponseSchema }),
    regenerate: (id: string): Promise<RegenerateAssetResponse> =>
      apiRequest(`/assets/${id}/regenerate`, {
        method: 'POST',
        schema: regenerateAssetResponseSchema,
      }),
    selectTake: (id: string, body: SelectTakeRequest): Promise<SelectTakeResponse> =>
      apiRequest(`/assets/${id}/select-take`, {
        method: 'POST',
        body,
        schema: selectTakeResponseSchema,
      }),
    takes: (id: string): Promise<ListTakesResponse> =>
      apiRequest(`/assets/${id}/takes`, { schema: listTakesResponseSchema }),
  },

  runs: {
    abort: (id: string, reason: 'cancelled_by_user' = 'cancelled_by_user'): Promise<AbortRunResponse> =>
      apiRequest(`/runs/${id}/abort`, {
        method: 'POST',
        body: { reason },
        schema: abortRunResponseSchema,
      }),
  },
};
